import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";

const NearMouseTooltip = ({ content, children }) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const [tooltipSize, setTooltipSize] = useState({ width: 0, height: 0 });
  const tooltipRef = useRef(null);
  const timeoutRef = useRef(null);
  const triggerRef = useRef(null);

  const updateCoords = (e) => {
    setCoords({ x: e.clientX, y: e.clientY });
  };

  const handleMouseEnter = (e) => {
    updateCoords(e);
    setVisible(true);
    resetTimeout(); // Start/refresh the timeout
  };

  const handleMouseMove = (e) => {
    updateCoords(e);
    resetTimeout(); // Reset timeout on movement
  };

  const handleMouseLeave = () => {
    setVisible(false);
    clearTimeout(timeoutRef.current);
  };

  const resetTimeout = () => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setVisible(false);
    }, 3000); // Auto-hide after 3 seconds
  };

  useEffect(() => {
    if (visible && tooltipRef.current) {
      const rect = tooltipRef.current.getBoundingClientRect();
      setTooltipSize({ width: rect.width, height: rect.height });
    }
  }, [visible, content]);

  useEffect(() => {
    return () => {
      // Clean up timeout on unmount
      clearTimeout(timeoutRef.current);
    };
  }, []);

  const { top, left } = useMemo(() => {
    const padding = 10;
    const maxWidth = 250;

    let top = coords.y - tooltipSize.height - padding;
    let left = coords.x + padding;

    const willOverflowRight = left + tooltipSize.width > window.innerWidth;
    const willOverflowTop = top < 0;

    if (willOverflowRight) {
      left = coords.x - (tooltipSize.width || maxWidth) - padding;
      if (left < padding) {
        left = padding;
      }
    }

    if (willOverflowTop) {
      top = coords.y + padding;
      if (top + tooltipSize.height > window.innerHeight - padding) {
        top = window.innerHeight - tooltipSize.height - padding;
      }
    }

    return { top, left };
  }, [coords, tooltipSize]);

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ display: "inline-block" }}
      >
        {children}
      </span>

      {visible &&
        createPortal(
          <div
            ref={tooltipRef}
            style={{
              position: "fixed",
              top,
              left,
              backgroundColor: "#C4C4C4",
              color: "#000",
              padding: "4px 8px",
              borderRadius: 4,
              fontSize: 12,
              lineHeight: 1.5,
              fontWeight: 600,
              zIndex: 9999,
              pointerEvents: "none",
              whiteSpace: "normal",
              wordWrap: "break-word",
              maxWidth: 250,
              maxHeight: 180,
              overflowY: "auto",
              boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
              opacity: visible ? 1 : 0,
              transform: "translateY(0px)",
              transition: "opacity 0.15s ease, transform 0.15s ease",
            }}
          >
            {content}
          </div>,
          document.body
        )}
    </>
  );
};

export default NearMouseTooltip;
